import { Component, OnInit, Output, EventEmitter, ViewChild, Input, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { QuestionGroupsService } from '../question-groups.service';
import { QuestionGroup } from '../../shared/models/question-group.model';
import { FeedbackService } from "../../shared/services/feedback.service";
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { MatDialog } from "@angular/material/dialog";
import { YesNo } from "../../shared/enums/yes-no.enum";
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntil, catchError, tap } from 'rxjs/operators';
import { Subject, of, forkJoin } from 'rxjs';

@Component({
  selector: 'app-save-question-group',
  templateUrl: './save-question-group.component.html',
  styleUrls: ['./save-question-group.component.scss'],
  providers: [QuestionGroupsService, FeedbackService]
})
export class SaveQuestionGroupComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @ViewChild('f', {static:true}) form: NgForm;
  @Output() complete: EventEmitter<void> = new EventEmitter();
  @Input() questionGroupId: number;
  questionGroup: QuestionGroup;
  isEditMode: boolean;
  title: string;

  constructor(
    private readonly questionGroupService: QuestionGroupsService,
    private readonly feedbackService: FeedbackService,
    private dialog: MatDialog) { }

  ngOnInit() {
    if (this.questionGroupId) {
      this.isEditMode = true;
      this.title = "Edit question group";
      this.getQuestionGroup();
    } else {
      this.isEditMode = false;
      this.title = "Create question group";
      this.questionGroup = new QuestionGroup();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  save() {
    if (this.form.valid) {
      if (this.questionGroupId) {
        this.questionGroupService
          .updateQuestionGroup(this.questionGroup)
          .pipe(
            tap(() => {
              this.feedbackService.success(`Question group saved.`);
              this.complete.emit();
            }),
            catchError((error) => {
              this.feedbackService.error(
                `An error occured while updating the question group'.`
              );
              console.error(error);
              return of(null);
            })
          )
          .subscribe();
      } else {
        this.questionGroupService.createQuestionGroup(this.questionGroup).pipe(
          tap(() => {
            this.feedbackService.success("Question group created");
            this.complete.emit();
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 409) {
              this.feedbackService.info(`Please try a different question group name. There is already another question group called ${this.questionGroup.name}.`);
            } else {
              this.feedbackService.error('An error occured while creating the question group');
            }
            return of(null);
          })
        ).subscribe();
      }
    }
  }

  delete() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent,
      {
        width: '250px',
        data: { title: name, message: `Are you sure you want to delete this question group?` }
      });

    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$),
      tap((result: YesNo) => {
        if (result === YesNo.Yes) {
          this.questionGroupService.deleteQuestionGroup(this.questionGroupId).pipe(
            takeUntil(this.destroy$),
            tap((response) => {
              if (response) {
                this.feedbackService.success('Question group deleted');
                this.complete.emit();
              }
            }),
            catchError(() => {
              this.feedbackService.error("An error occurred while deleting the Question group");
              return [];
            })
          ).subscribe();
        } else {
          this.dialog.closeAll();
        }
      })
    ).subscribe();
  }

  cancel() {
    this.complete.emit();
  }

  isSubmitDisabled() {
    return !this.form.valid;
  }

  private getQuestionGroup() {
    this.questionGroupService.getQuestionGroupById(this.questionGroupId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response) {
          this.questionGroup = response;
        }
      },
      error: () => {
        this.feedbackService.error('An error occured while trying to fetch the question group');
        this.cancel();
      }
    });
  }
}
